// frontend/src/components/lobby/AttendeeDeadEnd.tsx
import { Box, Button, Heading, Text, VStack } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { getAttendeeReturns } from '../../lib/attendeeReturnCredential'

/**
 * [LW-24 Slice 5] The attendee front-door surface, shown by every attendee page when its
 * magic-link token is bad/expired/revoked. Parity with the canvasser `Terminal`:
 *  - Tier 1: a friendly dead-end with a "Back to start" escape to the root chooser, so a
 *    stale link never strands the device.
 *  - Tier 2: if this device remembers a still-good token (from a prior successful arrival),
 *    a "Welcome back" card deep-links straight back to that schedule.
 * The current failing token is excluded from the welcome-back set, so a revoked stored token
 * can't loop back to this same dead-end. Root ("/") stays a static chooser — no auto-redirect.
 */
export function AttendeeDeadEnd({ currentToken }: { currentToken?: string }) {
  const returns = getAttendeeReturns().filter((c) => c.token !== currentToken)

  if (returns.length > 0) {
    return (
      <VStack minH="100vh" justify="center" py={12} px={6} gap={5} maxW="26rem" mx="auto" align="stretch">
        <Box>
          <Heading size="lg" mb={1}>Welcome back</Heading>
          <Text color="fg.muted">Pick up where you left off on this phone.</Text>
        </Box>
        {returns.map((c) => (
          <Button key={c.token} asChild colorPalette="brand" minH="44px">
            <RouterLink to={`/lobby/${c.token}`}>Continue to {c.eventName} →</RouterLink>
          </Button>
        ))}
        {/* [GI-10] Escape hatch even when this device remembers a token — the person may want a
            fresh link for a different address. */}
        <Button asChild variant="outline" minH="44px">
          <RouterLink to="/find-my-link">Email me my link</RouterLink>
        </Button>
        <Button asChild variant="outline" minH="44px">
          <RouterLink to="/">Back to start</RouterLink>
        </Button>
      </VStack>
    )
  }

  return (
    <VStack minH="100vh" justify="center" py={12} px={6} gap={4} maxW="26rem" mx="auto" align="stretch">
      <Text color="fg.muted" textAlign="center">
        This schedule link isn't active anymore. We can email you a new one.
      </Text>
      {/* [GI-10] Offer the self-service resend first, before "ask your organizer". */}
      <Button asChild colorPalette="brand" minH="44px">
        <RouterLink to="/find-my-link">Email me my link</RouterLink>
      </Button>
      <Button asChild variant="outline" minH="44px">
        <RouterLink to="/">Back to start</RouterLink>
      </Button>
    </VStack>
  )
}
